import type { Room } from '@brush/api-client';

/**
 * What the platform holds about this room. Read-only on purpose: branding, status and the three
 * feature switches are the platform administrator's to set, and a box an operator can type into
 * but not save is worse than a value they can only read.
 */

function Row({ label, value, tone = 'text-ink-950' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-ink-800/40">{label}</span>
      <span className={tone}>{value}</span>
    </div>
  );
}

function FeatureRow({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <Row
      label={label}
      value={enabled ? 'Enabled' : 'Disabled'}
      tone={enabled ? 'text-felt-700' : 'text-ink-800/50'}
    />
  );
}

export function RoomPane({ room }: { room: Room }) {
  return (
    <section className="max-w-3xl rounded-xl border border-ink-800/10 bg-white p-6 shadow-sm">
      <h2 className="font-display font-semibold text-ink-950">Room</h2>
      <p className="mt-0.5 text-xs text-ink-800/60">
        Managed by the platform admin. Contact them to change branding or enable a feature.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-y-2 text-xs sm:grid-cols-2 sm:gap-x-8">
        <Row label="Name:" value={room.name} />
        <Row label="Location:" value={room.location} />
        <Row label="Status:" value={room.status} />
      </div>

      <div className="mt-4 border-t border-ink-800/10 pt-4">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-ink-800/50">Features</div>
        <div className="grid grid-cols-1 gap-y-2 text-xs sm:grid-cols-2 sm:gap-x-8">
          {/* A disabled feature is stated rather than hidden: its section is greyed out in the
              top navigation, and this is the screen that says why. */}
          <FeatureRow label="Leaderboards:" enabled={room.canLeaderboard} />
          <FeatureRow label="Ratings:" enabled={room.canRatings} />
          <FeatureRow label="Fantasy:" enabled={room.canFantasy} />
        </div>
      </div>
    </section>
  );
}
